import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ChakraProvider,
    Flex,
    HStack,
    Text,
    Tooltip,
    Button,
} from '@chakra-ui/react';
import chakratheme from 'src/chakratheme';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import ActionsButton from 'components/tables/AGGrid/ActionsButton';
import useColumnState from 'hooks/useColumnState';
import { useQuery } from 'hooks/useCustomQuery';
import { PlatformEnums } from 'utils/platforms';
import StateToasts from './StateToasts';
import EightByEightCatalogCreate from './Create';
import EightByEightCatalogEdit from './Edit';
import walkthroughIds from './walkthroughIds';
import { CATALOG_COLUMNS } from './consts';
import { getCatalogItems } from './api';

const { ufAddCatalogItemBtn } = walkthroughIds;

function CatalogHeader({ setCatalogAction }) {
    return (
        <div style={{ paddingLeft: '20px' }}>
            <HStack spacing={2}>
                <Tooltip
                    label="Add Offering"
                    placement="bottom"
                    hasArrow
                    portalProps={{ containerRef: undefined }}>
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                        }}>
                        <Button
                            size="sm"
                            width="140px"
                            onClick={() => setCatalogAction('Create')}
                            data-walkthroughid={ufAddCatalogItemBtn}>
                            + Add Offering
                        </Button>
                    </div>
                </Tooltip>
            </HStack>
        </div>
    );
}

export default function EightByEightCatalogIndex() {
    const [selectedRowData, setSelectedRowData] = useState();
    const [catalogAction, setCatalogAction] = useState('Get');
    const [successAction, setSuccessAction] = useState(null);
    const tableRef = useRef(null);

    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);

    const platformId = PlatformEnums.EIGHTBYEIGHT;

    const {
        data: catalogItems = [],
        isLoading: isCatalogLoading,
        isFetching: isCatalogFetching,
        refetch,
    } = useQuery({
        queryKey: ['8x8CatalogItems', platformId],
        queryFn: () =>
            getCatalogItems(platformId, requiredScope).then((r) => r.data),
        staleTime: 0,
        cacheTime: 0,
        enabled: !!platformId,
        refetchOnWindowFocus: false,
    });

    const isLoading = isCatalogLoading;

    // Wrap action setter so returning to 'Get' always triggers a fresh fetch
    const handleSetCatalogAction = (action) => {
        setCatalogAction(action);
        if (action === 'Get') refetch();
    };

    const baseColumns = useMemo(
        () => [
            ...CATALOG_COLUMNS.slice(0, -1),
            {
                ...CATALOG_COLUMNS[CATALOG_COLUMNS.length - 1],
                cellRenderer: (params) => (
                    <ActionsButton
                        actions={[
                            {
                                label: 'Edit',
                                onClick: () => editCatalogItem(params.data),
                            },
                        ]}
                    />
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataObj = useMemo(
        () => ({
            columns,
            data: catalogItems,
        }),
        [columns, catalogItems],
    );

    function editCatalogItem(rowData) {
        setSelectedRowData(rowData);
        setCatalogAction('Edit');
    }

    return (
        <ChakraProvider theme={chakratheme} resetCSS={true}>
            {catalogAction === 'Get' && (
                <>
                    <StateToasts type={successAction} />
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        8×8 Catalog
                    </Text>
                    <AGGridMain
                        gridRef={tableRef}
                        tableData={tableDataObj}
                        masterDetail={false}
                        isLoading={isLoading}
                        isFetching={isCatalogFetching}
                        titleName="8x8 Catalog"
                        refetch={refetch}
                        onColumnVisible={onColumnVisible}
                        extraComponents={
                            <Flex gap="4">
                                <CatalogHeader
                                    setCatalogAction={handleSetCatalogAction}
                                />
                            </Flex>
                        }
                    />
                </>
            )}
            {catalogAction === 'Create' && (
                <EightByEightCatalogCreate
                    setCatalogAction={handleSetCatalogAction}
                    setSuccessAction={setSuccessAction}
                    platformId={platformId}
                    requiredScope={requiredScope}
                />
            )}
            {catalogAction === 'Edit' && (
                <EightByEightCatalogEdit
                    rowData={selectedRowData}
                    setCatalogAction={handleSetCatalogAction}
                    setSuccessAction={setSuccessAction}
                    darkMode={darkMode}
                    platformId={platformId}
                    requiredScope={requiredScope}
                />
            )}
        </ChakraProvider>
    );
}
